import { Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';

import { IconButton } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import useCorrectionStore from '@correction/hooks/useCorrectionStore.ts';

const PLAYBACK_DELAY = 460;

export function TreatmentCycles() {
  const { jaws } = useCorrectionStore();

  const totalLowerCycles = jaws.lowerJaw.getTransformIterations().getIterationsCount();
  const totalUpperCycles = jaws.upperJaw.getTransformIterations().getIterationsCount();
  const totalCycles = Math.max(totalLowerCycles, totalUpperCycles);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(totalCycles > 0 ? totalCycles - 1 : -1);

  useEffect(() => {
    setCurrentCycle(totalCycles > 0 ? totalCycles - 1 : -1);
  }, [totalCycles]);

  useEffect(() => {
    if (!isPlaying || totalCycles === 0) return;

    const id = window.setInterval(() => {
      setCurrentCycle((cycle) => {
        const next = cycle + 1;

        if (next >= totalCycles) {
          window.clearInterval(id);
          setIsPlaying(false);
          return totalCycles > 0 ? totalCycles - 1 : -1;
        }

        if (next <= totalUpperCycles) {
          jaws.upperJaw.applyIterationTransformations(next);
        }
        if (next <= totalLowerCycles) {
          jaws.lowerJaw.applyIterationTransformations(next);
        }

        return next;
      });
    }, PLAYBACK_DELAY);

    return () => window.clearInterval(id);
  }, [isPlaying, totalCycles, totalUpperCycles, totalLowerCycles, jaws.lowerJaw, jaws.upperJaw]);

  const handleTogglePlayback = () => {
    if (totalCycles === 0) return;
    if (!isPlaying) {
      jaws.upperJaw.resetToInitialTransforms();
      jaws.lowerJaw.resetToInitialTransforms();
    }
    setCurrentCycle(-1);
    setIsPlaying((value) => !value);
  };

  return (
    <section className="flex w-fit max-w-full flex-col gap-3 rounded-2xl border border-white/8 bg-content/95 p-3.5 text-white shadow-lg shadow-black/40 backdrop-blur">
      <header className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">Treatment cycles</span>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold leading-none text-white tabular-nums">{totalCycles || 0}</span>
            <span className="text-xs text-white/55">Preview jaw adjustments sequentially.</span>
          </div>
        </div>

        <IconButton
          aria-label={isPlaying ? 'Pause treatment cycle playback' : 'Play treatment cycle playback'}
          onClick={handleTogglePlayback}
          variant={!isPlaying ? 'ghost-success-glow' : 'ghost-danger-glow'}
          size="icon"
          disabled={totalCycles === 0}
          className={cn(
            'group h-10 w-10 rounded-full border border-white/10 bg-content-dark/60 backdrop-blur transition-all duration-300 [&_svg]:size-5',
            isPlaying
              ? 'text-red-400 hover:border-red-400 hover:bg-red-500/20 hover:text-red-300'
              : 'text-green-400 hover:border-green-400 hover:bg-green-500/20 hover:text-green-300',
            totalCycles === 0 && 'cursor-not-allowed opacity-50',
          )}
        >
          {isPlaying ? <Pause /> : <Play />}
        </IconButton>
      </header>

      <div className="relative">
        <div className="flex gap-2.5 overflow-x-auto px-2 py-3 [scrollbar-width:thin]">
          {totalCycles === 0 ? (
            <div className="flex rounded-xl border border-white/8 bg-content-dark/60 px-4 py-3 text-xs text-white/55">
              No treatment cycles yet.
            </div>
          ) : (
            <LayoutGroup id="treatment-cycles">
              {Array.from({ length: totalCycles }, (_, index) => {
                const isActive = index === currentCycle;

                return (
                  <motion.article
                    layout
                    initial={false}
                    key={index}
                    animate={{
                      y: isActive ? -6 : 0,
                      scale: isActive ? 1 : 0.97,
                    }}
                    whileHover={!isActive ? { y: -4 } : undefined}
                    transition={{
                      type: 'spring',
                      stiffness: 380,
                      damping: 30,
                      mass: 0.8,
                    }}
                    className={cn(
                      'group relative flex w-32 shrink-0 flex-col gap-2.5 rounded-xl border border-white/8 bg-content-dark/60 px-3 py-2.5 text-white transition-colors duration-300 backdrop-blur-sm',
                      isActive
                        ? 'border-primary/70 shadow-md shadow-primary/25'
                        : 'hover:border-primary/30 hover:shadow-md hover:shadow-primary/10',
                    )}
                  >
                    <AnimatePresence>
                      {isActive && (
                        <motion.span
                          layoutId="cycle-active-glow"
                          className="pointer-events-none absolute -inset-1 -z-10 rounded-[28px] bg-gradient-to-br from-primary/25 via-primary/10 to-transparent blur-[6px]"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.97 }}
                          transition={{ duration: 0.275, ease: 'easeOut' }}
                        />
                      )}
                    </AnimatePresence>

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-white/90">Cycle {index + 1}</span>
                      {isActive && (
                        <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-sm shadow-primary/50" />
                      )}
                    </div>

                    <div className="flex flex-col gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-10 text-[10px] uppercase tracking-wide text-white/55">Upper</span>
                        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                          <div
                            className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-primary"
                            style={{ transform: `scaleX(${index <= totalUpperCycles ? 1 : 0})`, transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)' }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="w-10 text-[10px] uppercase tracking-wide text-white/55">Lower</span>
                        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                          <div
                            className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-primary"
                            style={{ transform: `scaleX(${index <= totalLowerCycles ? 1 : 0})`, transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)' }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </LayoutGroup>
          )}
        </div>
      </div>
    </section>
  );
}
